import { cloneDeep, defaultsDeep, isEqual } from 'lodash'

import { initAddress } from '../../../libs/address.lib'
import { Customer, CustomerShippingAddress } from '../models/customer.entity'

// Initial states

const CUSTOMER_INITIAL_STATE: Partial<Customer> = {
  billingAddress: initAddress(),
  shippingAddress: [],
}

// Init

/**
 * Initialize a partial customer
 * @param customer - the partial customer
 * @returns the customer updated
 */
export function initCustomer(customer: Partial<Customer> = {}): Customer {
  return defaultsDeep(cloneDeep(customer), CUSTOMER_INITIAL_STATE)
}

// Addresses

/**
 * Initialize a customer shipping address
 * @param customer - the customer
 * @returns the shipping address
 */
export function initCustomerShippingAddress(
  customer: Customer,
): CustomerShippingAddress {
  return {
    name: customer.name,
    businessName: customer.businessName,
    address1: customer.billingAddress.address1,
    address2: customer.billingAddress.address2,
    city: customer.billingAddress.city,
    province: customer.billingAddress.province,
    postalCode: customer.billingAddress.postalCode,
    countryCode: customer.billingAddress.countryCode,
    phone: customer.phone,
    email: customer.email,
    isDefault: true,
  }
}

/**
 * Checks whether a customer has a shipping address
 * @param customer - the customer
 * @returns the updated customer
 */
export function checkShippingAddress(customer: Customer): Customer {
  if (customer.shippingAddress?.length > 0) {
    return customer
  }

  return {
    ...customer,
    shippingAddress: [initCustomerShippingAddress(customer)],
  }
}

/**
 * Adds a new shipping address to a shipping addresses list
 * @param addresses - the list of addresses
 * @param address - the address to be added
 * @returns the list of shipping addresses
 */
export function addAddress(
  addresses: CustomerShippingAddress[],
  address: CustomerShippingAddress,
): CustomerShippingAddress[] {
  return [...addresses, address]
}

/**
 * Updates a shipping address to a shipping addresses list
 * @param addresses - the list of addresses
 * @param address - the updated address
 * @param addressIndex - the index at which the address to be updated will be found
 * @returns the list of shipping addresses
 */
export function updateAddress(
  addresses: CustomerShippingAddress[],
  address: CustomerShippingAddress,
  addressIndex: number,
): CustomerShippingAddress[] {
  return addresses.map((a, i) => (i === addressIndex ? address : a))
}

/**
 * Removes a shipping address from a shipping addresses list
 * @param addresses - the list of addresses
 * @param addressIndex - the index at which the address to be removed will be found
 * @returns the list of shipping addresses
 */
export function removeAddress(
  addresses: CustomerShippingAddress[],
  addressIndex: number,
): CustomerShippingAddress[] {
  return addresses.filter((a, i) => i !== addressIndex)
}

/**
 * Sets a shipping address as default
 * @param addresses - the list of addresses
 * @param addressUpdated - the updated address
 * @returns the list of shipping addresses
 */
export function setDefaultAddress(
  addresses: CustomerShippingAddress[],
  addressUpdated: CustomerShippingAddress,
): CustomerShippingAddress[] {
  if (!addressUpdated.isDefault) {
    return addresses
  }

  return addresses.map((a) =>
    isEqual(a, addressUpdated) ? addressUpdated : { ...a, isDefault: false },
  )
}

/**
 * Updates the given list of addresses
 * @param addresses - the list of addresses
 * @param addr - the Observable address
 * @param address - the original address to update
 * @returns the updated list of addresses
 */
export function updateAddresses(
  addresses: CustomerShippingAddress[],
  addr: CustomerShippingAddress,
  address?: CustomerShippingAddress,
  index?: number,
): CustomerShippingAddress[] {
  const addressIndex = index ?? addresses.findIndex((a) => isEqual(a, address))
  return addressIndex >= 0
    ? updateAddress(addresses, addr, addressIndex)
    : addAddress(addresses, addr)
}

/**
 * Updates the given list of addresses
 * @param addresses - the list of addresses
 * @param address - the original address to delete
 * @returns the updated list of addresses
 */
export function deleteAddress(
  address: CustomerShippingAddress,
  addresses: CustomerShippingAddress[],
): CustomerShippingAddress[] {
  const addressIndex = addresses.findIndex((a) => isEqual(a, address))
  return removeAddress(addresses, addressIndex)
}
